import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Minus } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import AppCard from "../components/AppCard";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import Marquee from "../components/Marquee";
import { homeIntro, applications, bridgeSection, news } from "../mock";

const steps = [
  { title: "Tasarım & Mühendislik", text: "Proje ve statik çözümlerde optimum tasarım ve işlenebilirlik analizi." },
  { title: "İmalat & Üretim", text: "Plazma/lazer kesim, otomatik kumlama-boyama ve tozaltı kaynak üniteleri." },
  { title: "Kalite & NDT Kontrolü", text: "%100 NDT kontrolü ve tam izlenebilirlik ile kalite güvencesi." },
  { title: "Saha Montajı & Teslim", text: "Sahada montaj ve zamanında teslimat ile anahtar teslim çözüm." }
];

export default function Home() {
  return (
    <div className="bg-white" data-testid="home-page">
      <HeroSlider />

      {/* Intro — editorial two column */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="frame">
                <img
                  src="/images/apps/celik-konstruksiyon.jpg"
                  alt="Çelik konstrüksiyon"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
              </div>
              <div className="hidden sm:block absolute -bottom-10 -right-6 bg-white p-3 shadow-[0_30px_60px_-30px_rgba(12,19,34,0.45)]">
                <img src="/images/misc/hakkimizda.jpg" alt="İmalat" className="w-52 h-40 object-cover" />
              </div>
              <div className="absolute -top-px -left-px bg-brand-accent text-white px-6 py-5">
                <div className="font-display font-light text-3xl leading-none tabular-nums">20+</div>
                <div className="mt-2 font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/70">Yıllık Tecrübe</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-6 lg:pl-6">
            <SectionHeading eyebrow={homeIntro.eyebrow} title={homeIntro.title} />
            <div className="mt-8 space-y-5 text-[14.5px] text-brand-muted leading-[1.85] font-light max-w-xl">
              {homeIntro.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <Link
              to="/kurumsal"
              data-testid="home-about-btn"
              className="mt-10 group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-ink hover:text-brand-accent transition-colors duration-300"
            >
              <span className="w-10 h-px bg-brand-ink/25 group-hover:w-14 group-hover:bg-brand-accent transition-all duration-500" />
              Hakkımızda
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Reference marquee */}
      <section className="bg-brand-paper py-14 border-y border-brand-line overflow-x-clip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-center eyebrow text-brand-muted/80 mb-9">Bize Güvenen Kurumlar</p>
          <Marquee />
        </div>
      </section>

      {/* Applications */}
      <section className="bg-brand-paper py-24 md:py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <SectionHeading
              eyebrow="Hizmetlerimiz"
              title="Uygulama Alanlarımız"
              subtitle="Çelikle hayat bulan projelerde uçtan uca imalat ve montaj çözümleri sunuyoruz."
              className="max-w-xl"
            />
            <Link
              to="/uygulamalar"
              data-testid="home-all-applications-link"
              className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-accent whitespace-nowrap"
            >
              Tüm Uygulamalar
              <span className="w-9 h-9 rounded-full border border-brand-accent/30 flex items-center justify-center transition-[background-color,transform] duration-300 group-hover:bg-brand-accent group-hover:rotate-45">
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:text-white transition-colors duration-300" />
              </span>
            </Link>
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-line">
            {applications.slice(0, 6).map((app, i) => (
              <Reveal key={app.slug} delay={(i % 3) * 110}>
                <AppCard app={app} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process — dark architectural */}
      <section className="grain relative bg-brand-ink py-24 md:py-32 lg:py-40 overflow-hidden" data-testid="process-section">
        <div className="absolute inset-0 blueprint opacity-80" />
        <div className="glow pointer-events-none absolute -top-40 right-0 w-[700px] h-[700px] opacity-45" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionHeading
              dark
              eyebrow="Nasıl Çalışıyoruz"
              title="Fikirden Teslimata Kusursuz Süreç"
              subtitle="EN 1090-2, AWS ve TSE standartlarına uygun, %100 izlenebilir bir üretim akışı."
              className="max-w-xl"
            />
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08]">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 110}>
                <div className="group relative h-full bg-brand-ink px-8 py-10 transition-colors duration-500 hover:bg-white/[0.03]">
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[11px] tracking-[0.28em] text-brand-glow/70">{String(i + 1).padStart(2, "0")}</span>
                    <Minus className="w-5 h-5 text-white/15 group-hover:text-brand-glow/60 transition-colors duration-500" />
                  </div>
                  <h3 className="mt-10 font-display text-lg font-normal text-white tracking-[-0.02em]">{s.title}</h3>
                  <p className="mt-3.5 text-[13px] text-slate-500 leading-relaxed font-light">{s.text}</p>
                  <span className="absolute left-0 bottom-0 h-px w-0 bg-brand-glow/60 transition-all duration-700 group-hover:w-full" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bridge */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <Reveal className="order-2 lg:order-1 lg:col-span-6">
            <div className="relative">
              <div className="frame">
                <img src={bridgeSection.image} alt="Çelik köprü" className="w-full h-[420px] lg:h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -right-4 md:-right-8 bg-white border border-brand-line px-7 py-6 shadow-[0_30px_60px_-30px_rgba(12,19,34,0.4)] hidden sm:block">
                <div className="font-display font-light text-3xl text-brand-accent leading-none tabular-nums">%100</div>
                <div className="mt-2 font-mono text-[9.5px] uppercase tracking-[0.2em] text-brand-muted">NDT Kontrolü</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={150} className="order-1 lg:order-2 lg:col-span-6 lg:pl-6">
            <SectionHeading eyebrow={bridgeSection.eyebrow} title={bridgeSection.title} />
            <div className="mt-8 space-y-5 text-[14.5px] text-brand-muted leading-[1.85] font-light max-w-xl">
              {bridgeSection.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <ul className="mt-9 grid sm:grid-cols-2 gap-px bg-brand-line border border-brand-line">
              {bridgeSection.bullets.map((b, i) => (
                <li key={i} className="bg-white px-5 py-4 text-[13px] text-brand-ink font-medium flex items-center gap-3">
                  <span className="font-mono text-[10px] text-brand-accent/60">{String(i + 1).padStart(2, "0")}</span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/uygulamalar"
                className="group font-mono text-[11px] uppercase tracking-[0.18em] bg-brand-accent hover:bg-brand-accent-dark text-white pl-6 pr-3.5 py-3.5 rounded-full flex items-center gap-3 transition-colors duration-300"
              >
                Uygulamalar
                <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link
                to="/iletisim"
                className="font-mono text-[11px] uppercase tracking-[0.18em] border border-brand-line hover:border-brand-ink text-brand-ink px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                İletişime Geçin
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* News */}
      <section className="bg-brand-paper py-24 md:py-32 border-t border-brand-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-16">
            <SectionHeading eyebrow="Güncel" title="Bizden Haberler" />
            <Link
              to="/haberler"
              data-testid="home-all-news-link"
              className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-accent"
            >
              Tüm Haberler
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-brand-line">
            {news.map((n, i) => (
              <Reveal key={n.slug} delay={(i % 3) * 110}>
                <Link
                  to={`/haber/${n.slug}`}
                  data-testid={`news-card-${n.slug}`}
                  className="group flex flex-col h-full bg-white hover:shadow-[0_28px_50px_-28px_rgba(15,56,122,0.35)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1"
                >
                  <div className="frame relative aspect-[16/10] overflow-hidden">
                    <img
                      src={n.image}
                      alt={n.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.07]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 to-transparent" />
                    <span className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/85">{n.date}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display text-[17px] font-medium tracking-[-0.02em] text-brand-ink group-hover:text-brand-accent transition-colors duration-300 line-clamp-2">
                      {n.title}
                    </h3>
                    <p className="mt-3 text-[13.5px] text-brand-muted leading-relaxed font-light line-clamp-3 flex-1">{n.excerpt}</p>
                    <span className="mt-6 pt-5 border-t border-brand-line-soft inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-accent">
                      Devamını Oku <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
